import React, { createContext, useContext, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import appConfig from "configs/app.config";
import { Notification, Spinner, toast } from "components/ui";
import PDFViewerModal from "components/shared/PDFViewerModal";

import { ImSpinner9 } from "react-icons/im";
import IframeViewerModal from "components/shared/IframePDFViewerModal";

const PdfViewerContext = createContext(null);

export const PdfViewerProvider = ({ children }) => {
  const authToken = useSelector((s) => s.auth?.session?.token);

  const [fileUrl, setFileUrl] = useState(null);
  const [message, setMessage] = useState(null);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingFilePath, setPendingFilePath] = useState(null);

  // controls which viewer to show
  const [viewerType, setViewerType] = useState("pdf");

  const notifyNotFound = useCallback((text) => {
    toast.push(
      <Notification title="Not Found!" type="danger">
        {text ||
          "We regret to inform you that the file you requested is currently unavailable on our server."}
      </Notification>,
    );
  }, []);

  const sanitizeUrl = (url) => {
    if (!url || typeof url !== "string") return url;

    try {
      const parsed = new URL(url);

      // remove duplicate slashes from pathname
      parsed.pathname = parsed.pathname.replace(/\/{2,}/g, "/");

      return parsed.toString();
    } catch (err) {
      console.warn("Invalid URL format:", url);
      return url;
    }
  };

  const openPdf = useCallback(
    async (docOrPath) => {
      const urlPath =
        typeof docOrPath === "string"
          ? docOrPath
          : docOrPath?.url ||
            docOrPath?.fileUrl ||
            docOrPath?.file_path ||
            docOrPath?.path;

      if (!urlPath) {
        notifyNotFound();
        return;
      }

      const rawUrl =
        urlPath.startsWith("http://") || urlPath.startsWith("https://")
          ? urlPath
          : `${appConfig.apiPrefix}${urlPath}`;

      const finalUrl = sanitizeUrl(encodeURI(rawUrl));

      const isDirectFileUrl =
        finalUrl.includes(".pdf") ||
        finalUrl.includes("print") ||
        finalUrl.includes("get_file_content.php");

      setPendingFilePath(urlPath);
      setLoading(true);
      setMessage(null);

      try {
        /* JSON response */
        if (!isDirectFileUrl) {
          try {
            const jsonResp = await axios.get(finalUrl, {
              headers: {
                Authorization: authToken ? `Bearer ${authToken}` : undefined,
                "Content-Type": "application/json",
              },
            });

            const data = jsonResp?.data;

            if (data) {
              if (typeof data === "object" && data.fileUrl) {
                setFileUrl(sanitizeUrl(data.fileUrl));
                setViewerType("pdf");
                setVisible(true);
                return;
              }

              if (typeof data === "string" && data.startsWith("http")) {
                setFileUrl(sanitizeUrl(data));
                setViewerType("pdf");
                setVisible(true);
                return;
              }

              const candidate =
                typeof data === "object"
                  ? Object.values(data).find(
                      (v) => typeof v === "string" && v.startsWith("http"),
                    )
                  : null;

              if (candidate) {
                setFileUrl(sanitizeUrl(candidate));
                setViewerType("pdf");
                setVisible(true);
                return;
              }
            }
          } catch (jsonErr) {
            console.warn("JSON response failed → fallback to blob", jsonErr);
          }
        }

        /* Blob fallback */
        try {
          const blobResp = await axios.get(finalUrl, {
            headers: {
              Authorization: authToken ? `Bearer ${authToken}` : undefined,
            },
            responseType: "blob",
            withCredentials: true,
          });

          const blob = blobResp?.data;

          if (blob && blob.size > 0) {
            const objectUrl = window.URL.createObjectURL(blob);

            setFileUrl(objectUrl);
            setViewerType("pdf");
            setVisible(true);
            return;
          }
        } catch (blobErr) {
          console.warn("Blob fallback failed", blobErr);
        }

        notifyNotFound();
      } catch (err) {
        console.error("PdfViewerContext openPdf error:", err);
        notifyNotFound();
      } finally {
        setPendingFilePath(null);
        setLoading(false);
      }
    },
    [authToken, notifyNotFound],
  );

  const closePdf = useCallback(() => {
    try {
      if (
        fileUrl &&
        typeof fileUrl === "string" &&
        fileUrl.startsWith("blob:")
      ) {
        window.URL.revokeObjectURL(fileUrl);
      }
    } catch (e) {
      console.warn("Failed to revoke blob URL", e);
    }

    setFileUrl(null);
    setMessage(null);
    setVisible(false);
    setPendingFilePath(null);
    setLoading(false);
    setViewerType("pdf");
  }, [fileUrl]);

  return (
    <PdfViewerContext.Provider
      value={{
        openPdf,
        closePdf,
        visible,
        fileUrl,
        message,
        loading,
        pendingFilePath,
      }}
    >
      {children}

      {/* Loader */}
      {loading && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg px-6 py-5 shadow-lg flex flex-col items-center gap-3">
            <Spinner size={50} indicator={ImSpinner9} />
            <p className="text-sm font-medium text-gray-700">
              Opening document...
            </p>
          </div>
        </div>
      )}

      {/* Primary viewer */}
      {visible && fileUrl && viewerType === "pdf" && (
        <PDFViewerModal
          fileUrl={fileUrl}
          message={message}
          onClose={closePdf}
          onPdfError={() => {
            console.warn("react-pdf failed → switching to iframe viewer");
            setViewerType("iframe");
          }}
        />
      )}

      {/* Fallback viewer */}
      {visible && fileUrl && viewerType === "iframe" && (
        <IframeViewerModal fileUrl={fileUrl} onClose={closePdf} />
      )}
    </PdfViewerContext.Provider>
  );
};

export const usePdfViewer = () => {
  const ctx = useContext(PdfViewerContext);

  if (!ctx) {
    throw new Error("usePdfViewer must be used inside PdfViewerProvider");
  }

  return ctx;
};
